import React from 'react'
import styled from '@emotion/styled'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { signupTracker, trackNeedAssistance } from '@mobi/account/Common/Analytics'
import { useHistory } from '@mobi/account/Hooks/useHistory'
import { AppHeaderLogo } from '@mobi/component-library/Areas/AppHeader/AppHeaderLogo'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, layering, measurements, spacing } from '@mobi/component-library/Foundations'
import { callCenterPhoneNumber } from '@mobi/utils'
import { getSignUpState } from '../Store'
import { routes } from '../Utils'

const homeButtonDisabledRoutes = [
  routes.password,
  routes.phoneNumberVerification,
  routes.security,
  routes.depositLimit,
]

const backButtonRemovedRoutes = [
  routes.password,
  routes.phoneNumberVerification,
  routes.security,
  routes.depositLimit,
  routes.signUpComplete,
  routes.signUpUnsuccessful,
  routes.verificationAccountInformation,
]

export const SignUpPageHeader = ({
  isVerificationAccountHomePage,
}: {
  isVerificationAccountHomePage?: boolean
}): JSX.Element => {
  const history = useHistory()
  const location = useLocation()
  const navigate = useNavigate()
  const isFetching = useIsFetching()
  const isMutating = useIsMutating()
  const state = useSelector(getSignUpState)

  const isProcessing = isFetching > 0 || isMutating > 0

  const route = location.pathname
  const isBackRemoved = backButtonRemovedRoutes.includes(route) || isVerificationAccountHomePage
  const isHomeDisabled = homeButtonDisabledRoutes.includes(route) || isVerificationAccountHomePage
  const isHelpCenterVisible = route !== routes.verificationAccountInformation

  return (
    <SignUpPageHeaderStyled>
      <BackNavAndLogoContainer>
        <Button
          aria-label='navigate back'
          isIconOnlyButton
          onClick={() => {
            if (isBackRemoved || isProcessing) return

            const hasPreviousPage = location.key !== 'default'
            if (hasPreviousPage) {
              navigate(-1)
            } else {
              navigate('/')
            }
          }}
          color='tertiary_color'
          size='sm'
          style={{ display: isBackRemoved ? 'none' : 'flex' }}
        >
          <Icon name='SolidChevronLeft' color={colors.white} />
        </Button>

        <AppHeaderLogo
          onClick={e => {
            e.preventDefault()
            if (isHomeDisabled || isProcessing) return
            signupTracker({
              interaction_type: 'Go Back to Home Page',
              signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
              location: 'Header',
              signUpStarted: state.hasUserStartedSignup,
            })
            navigate('/')
          }}
          compactBreakpoint='px70'
          style={{ cursor: isHomeDisabled || isProcessing ? 'default' : 'pointer' }}
        />
      </BackNavAndLogoContainer>

      {isHelpCenterVisible && (
        <Button
          onClick={() => {
            if (isProcessing) return

            history.push(callCenterPhoneNumber)
            if (isVerificationAccountHomePage) {
              trackNeedAssistance('ID Verification Home')
            } else {
              signupTracker({
                interaction_type: 'Need Assistance',
                signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
                location: 'Header',
              })
            }
          }}
          color='secondary_dark'
          size='sm'
        >
          Need Help?
          <Icon name='LinePhoneCall01' />
        </Button>
      )}
    </SignUpPageHeaderStyled>
  )
}

const SignUpPageHeaderStyled = styled.header({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  maxWidth: measurements.mobi.maxWidth,
  margin: '0 auto',
  zIndex: layering.appHeader,
  backgroundColor: colors.studio[700],
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `0 ${spacing.sm}`,
  boxSizing: 'border-box',
  height: measurements.mobi.headerHeight,
})

const BackNavAndLogoContainer = styled.div({
  display: 'flex',
  justifyContent: 'start',
  '> :last-child': {
    paddingLeft: spacing.smx2,
  },
})
